import React, { useLayoutEffect, useMemo, useRef } from 'react';

import cn from 'classnames';

import { ICommentGroup, ICommentMessage, IMessageAttachment } from '@entities/message/model/types';
import { CircularLoader, Flex } from '@shared/ui';

import { DateChip } from './DateChip';
import { EmptyMessagesPlaceholder } from './EmptyMessagesPlaceholder';
import cls from './MessageChat.module.css';
import { MessageItem } from './MessageItem';
import { MessagesList } from './MessagesList';
import { UnreadDivider } from './UnreadDivider';

export interface IMessageChatProps {
    messagesData: ICommentGroup[];
    deciderText?: string;
    className?: string;
    innerRef?: React.Ref<HTMLElement>;
    footer?: React.ReactNode;
    isInitialLoading?: boolean;
    emptyPlaceholder?: React.ReactNode;
    onDeleteMessage?: (messageId: ICommentMessage['id']) => void;
    onOpenAttachments?: (
        attachments: IMessageAttachment[],
        currentAttachmentId: IMessageAttachment['id']
    ) => void;
}

export const MessageChat = ({
    className,
    innerRef,
    messagesData = [],
    footer,
    isInitialLoading = false,
    emptyPlaceholder,
    onDeleteMessage,
    onOpenAttachments,
    deciderText,
}: IMessageChatProps) => {
    const messagesListRef = useRef<HTMLUListElement | null>(null);
    const messagesCount = useMemo(
        () => messagesData.reduce((count, group) => count + (group.comments?.length || 0), 0),
        [messagesData]
    );
    const firstUnreadMessageId = useMemo(
        () =>
            messagesData
                .flatMap((group) => group.comments || [])
                .find((message) => message.isUnread)?.id || null,
        [messagesData]
    );

    useLayoutEffect(() => {
        if (!messagesListRef.current) {
            return;
        }

        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }, [messagesCount]);

    const renderContent = () => {
        if (isInitialLoading) {
            return (
                <div className={cls.LoaderContainer}>
                    <CircularLoader size={32} thickness={3} />
                </div>
            );
        }

        if (messagesCount === 0) {
            return emptyPlaceholder || <EmptyMessagesPlaceholder />;
        }

        return (
            <MessagesList ref={messagesListRef}>
                {messagesData.map((group) => (
                    <React.Fragment key={group.datetime}>
                        <DateChip datetime={group.datetime} className={cls.dateChip} />
                        {group?.comments?.map((message) => (
                            <React.Fragment key={message.id}>
                                {firstUnreadMessageId === message.id && (
                                    <UnreadDivider deciderText={deciderText} />
                                )}
                                <MessageItem
                                    messageData={message}
                                    onDeleteMessage={onDeleteMessage}
                                    onOpenAttachments={onOpenAttachments}
                                />
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </MessagesList>
        );
    };

    return (
        <section ref={innerRef} className={cn(cls.MessageChat, className)}>
            <Flex as="main" className={cls.MessagesViewport}>
                {renderContent()}
            </Flex>
            {footer && <div className={cls.Footer}>{footer}</div>}
        </section>
    );
};
